import { yupResolver, TransformedValues, useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useQueryClient, useMutation } from "@tanstack/react-query";
import { FirestoreError } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { Asserts } from "yup";
import useUserStore from "@/modules/users/store";
import { captureException } from "@/services/log";
import {
  EMBAIXADORES_PESSOAS_COLLECTION_KEY,
  TEmbaixadorPessoaForm,
  addEmbaixadorPessoa,
  updateEmbaixadorPessoa,
  checkEmailExists
} from "../firestore";
import embaixadorPessoaSchema from "../schema/embaixador-pessoa-schema";

type FormInputs = Asserts<typeof embaixadorPessoaSchema>;

/**
 *
 * @param embaixadorPessoa
 */
function useEmbaixadorPessoaForm(embaixadorPessoa: TEmbaixadorPessoaForm & Partial<Pick<TEmbaixadorPessoa, "id">>) {
  const activeEmpresaId = useUserStore((state) => state.activeEmpresaId);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const form = useForm<TEmbaixadorPessoaForm>({
    validate: yupResolver(embaixadorPessoaSchema),
    transformValues: (values) => embaixadorPessoaSchema.cast(values),
    initialValues: embaixadorPessoa
  });

  const { mutate, isPending, reset } = useMutation<void, FirestoreError, FormInputs>({
    mutationFn: async (values) => {
      if (!activeEmpresaId) {
        throw new Error("Nenhuma empresa selecionada");
      }

      // Verificar se o email já existe (apenas se email foi fornecido)
      if (values.email && values.email.trim()) {
        const emailExists = await checkEmailExists(activeEmpresaId, values.email.trim(), embaixadorPessoa.id);
        if (emailExists) {
          throw new Error("Já existe um embaixador com este email");
        }
      }

      return embaixadorPessoa.id
        ? updateEmbaixadorPessoa(activeEmpresaId, embaixadorPessoa.id, values)
        : addEmbaixadorPessoa(activeEmpresaId, values).then(() => {});
    },
    async onSuccess(_data, variables) {
      showNotification({
        message: `${variables.nome} ${embaixadorPessoa.id ? "atualizado" : "cadastrado"} com sucesso!`,
        color: "green",
        autoClose: 2500
      });
      reset();
      form.reset();
      await queryClient.invalidateQueries({
        queryKey: [...EMBAIXADORES_PESSOAS_COLLECTION_KEY]
      });
      navigate("/embaixadores");
    },
    onError(error) {
      captureException(error, true);
    }
  });

  type TFormTransformed = TransformedValues<typeof form>;

  /**
   *
   * @param values
   */
  function handleFormSubmit(values: TFormTransformed) {
    mutate(values);
  }

  return { form, isPending, onSubmit: form.onSubmit(handleFormSubmit) };
}

export default useEmbaixadorPessoaForm;
